import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import WebsiteLayout from "./WebsiteLayout";
import { useScrollReveal } from "./useAnimations";
import { ArrowRight, Clock, Calendar } from "lucide-react";

const CATEGORIES = [
  "All Insights",
  "Fund Operations",
  "Accounting",
  "Technology",
  "Compliance",
  "Governance",
  "Security",
];

const RESOURCES = [
  {
    slug: "modern-fund-accounting-infrastructure",
    type: "Insight",
    date: "Feb 10, 2026",
    title: "Why Modern Fund Accounting Needs Purpose-Built Infrastructure",
    excerpt: "Explore why generic accounting tools fall short for institutional fund operations and how purpose-built systems deliver accuracy, auditability, and scale.",
    readTime: "10 min read",
    category: "Accounting",
  },
  {
    slug: "operational-workflows-fund-administration",
    type: "Insight",
    date: "Feb 5, 2026",
    title: "Streamlining Operational Workflows in Fund Administration",
    excerpt: "How automated approval chains, request management, and centralized operations reduce errors and accelerate decision-making in fund management.",
    readTime: "8 min read",
    category: "Fund Operations",
  },
  {
    slug: "investor-onboarding-best-practices",
    type: "Publication",
    date: "Jan 28, 2026",
    title: "Investor Onboarding: From Manual Checklists to Guided Workflows",
    excerpt: "A practical guide to modernizing investor onboarding with structured workflows, KYC/AML integration, and transparent status tracking.",
    readTime: "9 min read",
    category: "Fund Operations",
  },
  {
    slug: "general-ledger-investment-funds",
    type: "Insight",
    date: "Jan 22, 2026",
    title: "The Role of a Structured General Ledger in Investment Fund Accounting",
    excerpt: "Understanding how a multi-level chart of accounts and hierarchical ledger design supports complex fund structures and regulatory reporting.",
    readTime: "11 min read",
    category: "Accounting",
  },
  {
    slug: "data-security-financial-platforms",
    type: "Insight",
    date: "Jan 15, 2026",
    title: "Data Security Standards for Institutional Financial Platforms",
    excerpt: "How end-to-end encryption, role-based access controls, and continuous monitoring protect sensitive financial data across fund operations.",
    readTime: "8 min read",
    category: "Security",
  },
  {
    slug: "regulatory-compliance-fund-reporting",
    type: "Publication",
    date: "Jan 10, 2026",
    title: "Meeting Regulatory Compliance Through Automated Fund Reporting",
    excerpt: "How institutional-quality reporting engines help fund managers stay ahead of evolving regulatory requirements while reducing manual effort.",
    readTime: "9 min read",
    category: "Compliance",
  },
  {
    slug: "audit-trail-importance-fund-governance",
    type: "Insight",
    date: "Jan 5, 2026",
    title: "Why Complete Audit Trails Are Non-Negotiable for Fund Governance",
    excerpt: "A deep dive into how comprehensive activity logging, version history, and event tracking build the foundation for institutional trust.",
    readTime: "7 min read",
    category: "Governance",
  },
  {
    slug: "scalable-architecture-fund-technology",
    type: "Insight",
    date: "Dec 18, 2025",
    title: "Building Scalable Architecture for Fund Management Technology",
    excerpt: "Key architectural decisions that enable fund technology platforms to handle growing complexity without sacrificing performance or reliability.",
    readTime: "10 min read",
    category: "Technology",
  },
  {
    slug: "document-management-fund-operations",
    type: "Publication",
    date: "Dec 12, 2025",
    title: "Centralized Document Management: A Pillar of Fund Operations",
    excerpt: "How centralized repositories with granular permissions, version control, and audit transparency transform document workflows in fund administration.",
    readTime: "8 min read",
    category: "Fund Operations",
  },
  {
    slug: "role-based-access-control-financial-systems",
    type: "Insight",
    date: "Dec 5, 2025",
    title: "Designing Role-Based Access Control for Financial Systems",
    excerpt: "Practical approaches to implementing granular permissions that protect sensitive data while maintaining operational efficiency across user roles.",
    readTime: "9 min read",
    category: "Security",
  },
  {
    slug: "period-end-close-automation",
    type: "Insight",
    date: "Nov 28, 2025",
    title: "Automating Period-End Close Workflows in Fund Accounting",
    excerpt: "How automated close processes reduce turnaround time, minimize errors, and deliver reliable financial statements for fund stakeholders.",
    readTime: "10 min read",
    category: "Accounting",
  },
  {
    slug: "governance-frameworks-institutional-investors",
    type: "Publication",
    date: "Nov 20, 2025",
    title: "What Institutional Investors Expect from Fund Governance Frameworks",
    excerpt: "A practical breakdown of governance standards that institutional allocators evaluate during due diligence and ongoing monitoring.",
    readTime: "8 min read",
    category: "Governance",
  },
];

export const RESOURCE_DATA = RESOURCES;

export default function ResourcesPage() {
  const [activeCategory, setActiveCategory] = useState("All Insights");
  useScrollReveal();

  const filtered = useMemo(() => {
    if (activeCategory === "All Insights") return RESOURCES;
    return RESOURCES.filter(r => r.category === activeCategory);
  }, [activeCategory]);

  return (
    <WebsiteLayout>
      {/* Banner */}
      <div className="w-banner-dark">
        <section className="w-banner w-reveal" data-testid="resources-banner" style={{ paddingBottom: "4rem" }}>
          <div className="w-label">Knowledge Center</div>
          <h1 className="w-title" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>
            News &amp; <strong>Insights</strong>
          </h1>
          <p className="w-subtitle" style={{ maxWidth: 560 }}>
            Perspectives on fund operations, accounting infrastructure, technology, 
            and institutional best practices.
          </p>
        </section>
      </div>

      {/* Content */}
      <section className="w-section" style={{ paddingTop: "3rem" }}>
        {/* Category filters */}
        <div className="w-resource-filters w-reveal" data-testid="resource-filters">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`w-resource-filter ${activeCategory === cat ? "active" : ""}`}
              onClick={() => setActiveCategory(cat)}
              data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Result count */}
        <div className="w-resource-count w-reveal w-delay-1" data-testid="resource-count">
          Displaying {filtered.length} of {RESOURCES.length} insights
        </div>

        {/* Article grid */}
        <div className="w-resource-grid" data-testid="resource-grid">
          {filtered.map((resource, i) => (
            <Link
              key={resource.slug}
              to={`/resources/${resource.slug}`}
              className={`w-resource-card w-reveal w-delay-${(i % 3) + 1}`}
              data-testid={`resource-card-${resource.slug}`}
            >
              <div className="w-resource-card-type">{resource.type}</div>
              <div className="w-resource-card-date">
                <Calendar size={13} />
                {resource.date}
              </div>
              <h3 className="w-resource-card-title">{resource.title}</h3>
              <p className="w-resource-card-excerpt">{resource.excerpt}</p>
              <div className="w-resource-card-meta">
                <span className="w-resource-card-readtime">
                  <Clock size={13} />
                  {resource.readTime}
                </span>
                <span className="w-resource-card-category">{resource.category}</span>
              </div>
              <div className="w-resource-card-arrow">
                <ArrowRight size={16} />
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="w-reveal" style={{ textAlign: "center", padding: "4rem 0" }}>
            <p style={{ color: "var(--w-text-dim)", fontSize: "1.0625rem" }}>
              No insights found for this category.
            </p>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="w-cta-section">
        <div className="w-reveal">
          <h2 className="w-title" style={{ maxWidth: 500, margin: "0 auto 1rem" }}>
            Have a <strong>Question</strong>?
          </h2>
          <p className="w-subtitle" style={{ margin: "0 auto 2rem", maxWidth: 440 }}>
            If a topic discussed here is relevant to your fund, our team can help translate perspective into operational reality.
          </p>
          <Link to="/contact" className="w-btn w-btn-primary" data-testid="resources-cta-btn">
            Make an Enquiry <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <div style={{ 
        maxWidth: "var(--w-max-width)", margin: "0 auto", padding: "2rem 3rem 3rem",
        fontSize: "0.75rem", color: "var(--w-text-dim)", lineHeight: 1.6
      }}>
        All content published on Tychi Insights is for general informational purposes only and does not constitute 
        legal, tax, investment, or regulatory advice. Readers should seek professional guidance appropriate to their 
        specific circumstances.
      </div>
    </WebsiteLayout>
  );
}
